import React, { useEffect, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import {
  CircularProgress,
  TextField,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import MainContext from "../../context/main/mainContext";
import getMemberCount from "../../utilities/getMemberCount";
import planLookup from "../../utilities/planLookup";
import useCommonMemberAndTeamAction from "../../hooks/useCommonMemberAndTeamActions";
import IntegrationsSection from "../../components/IntegrationsSection";
import StripeAddPaymentModal from "../../checkout/components/StripeAddPaymentModal";
import ExtendTrialButton from "./ExtendTrialButton";
import LeadCaptureUpgradeSection from "./LeadCaptureSection";
import dates from "../../utilities/dates";
import { extractSubItemsFromTeam } from "../../utilities/extractSubItemsFromTeam";
import { newBillingPriceConfig } from "../../checkout/config";

function getModalStyle() {
  return {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "65%",
    display: "block",
    "overflow-y": "auto",
    "max-height": "80%",
  };
}

const useStyles = makeStyles((theme) => ({
  paper: {
    position: "absolute",
    width: 400,
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
}));

export default function TeamModal(props) {
  const mainContext = useContext(MainContext);
  const {
    snackbarAction,
    user,
    getTeamById,
    team,
    setMemberAdmin,
    getPriceConfig,
    price_config,
    changePlan,
    addTeamOrder,
    isProd,
    getTeamIntegrationConnections,
    integrationConnections,
    getTeamSalesforceData,
    teamSalesforceData,
    resetTeamState,
    getTeamCrmExportLogs,
    crmExportLogs,
    stripe_customer_payment_methods,
    getCustomerPaymentMethods,
  } = mainContext;

  const {
    isTextFollowUp,
    isEmailFollowUp,
    email,
    editEmailOpen,
    handleSetEmailOpen,
    handleEmailChange,
    isSendWeeklyReminder,
    isCommonActionLoading,
    handleUpdateTeam,
  } = useCommonMemberAndTeamAction("team");
  const classes = useStyles();
  const [modalStyle] = React.useState(getModalStyle);

  const [selectedPlanInterval, setSelectedPlanInterval] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [internalDemoTeam, setInternalDemoTeam] = useState({});
  const [isPriceMonthly, setIsPriceMonthly] = useState(false);
  const [adminControlsExpanded, setAdminControlsExpanded] = useState(false);
  const [integrationsExpanded, setIntegrationsExpanded] = useState(false);
  const [leadCaptureUpgradeExpanded, setLeadCaptureUpgradeExpanded] = useState(false);
  const [isIntegrationsLoading, setIsIntegrationsLoading] = useState(false);
  const [isCrmExportLogsLoading, setIsCrmExportLogsLoading] = useState(false);
  const [openStripePaymentModal, setOpenStripePaymentModal] = useState(false);
  const [teamSubscriptionFormValues, setTeamSubscriptionFormValues] = useState({
    stripeSubscription: "",
    enterprisePlan: "",
  });

  useEffect(
    () => () => {
      resetTeamState();
    },
    [],
  );

  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    // These keys are public
    loadStripe(
      isProd
        ? "pk_live_51ISRsnJqkGKmOFO6LLM2GcznWFLHS40RpqGm5N58il5MgKyT3XbfMw0r3jgbpGRcyq24rtr3YrK8Bbd4DaJTO7Pm00dmW3rNlq"
        : "pk_test_51ISRsnJqkGKmOFO6YXwekNU7nltG5qZLswJWgSyQnC78FFt8d5ZxveEdy9bqmVbVPMkxAjeqdc17Wy7sCxYlAPnK00eb2dGR0l",
    ).then((res) => {
      setStripePromise(res);
    });
  }, []);

  useEffect(() => {
    const { seatsItem } = extractSubItemsFromTeam(team);
    const { interval } = seatsItem?.price?.recurring || {};
    const { interval_count: intervalCount } = seatsItem?.price?.recurring || {};

    setSelectedPlanInterval(
      newBillingPriceConfig.find(
        (config) => config.interval === interval && config.intervalCount === intervalCount,
      ),
    );
    setIsPriceMonthly(interval === "month" && intervalCount === 1);
  }, [team?.stripeCustomer]);

  useEffect(() => {
    if (team?.stripeCustomer?.id) {
      getCustomerPaymentMethods(
        {
          stripeCustomerId: team?.stripeCustomer.id,
        },
        (err) => {
          if (err) {
            alert(err);
          }
        },
      );
    }
  }, [team?.stripeCustomer]);

  useEffect(() => {
    setTeamSubscriptionFormValues({
      stripeSubscription: team?.team?.stripeSubscription || "",
      enterprisePlan: team?.team?.enterprisePlan || "",
    });

    (async () => {
      if (!team?.team?.id) return;
      setIsLoading(true);
      await getTeamSalesforceData(team?.team?.id, () => {});
      setIsLoading(false);
    })();
  }, [team?.team]);

  const handleSubscriptionFormChange = (e) => {
    const { name, value } = e.target;
    setTeamSubscriptionFormValues({
      ...teamSubscriptionFormValues,
      [name]: value,
    });
  };

  const handleSubscriptionFormSubmit = async (e) => {
    e.preventDefault();
    const isConfirmed = window.confirm(
      "Are you sure you want to update this info? Incorrect updates may cause issues",
    );

    if (!isConfirmed) return;
    setIsLoading(true);

    try {
      await handleUpdateTeam(teamSubscriptionFormValues);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const internalDemoTeam = team?.team?.internalDemoTeams?.find(
      (demoTeam) => !demoTeam.isDeleted && !demoTeam.hasPaid && !demoTeam.isExpired,
    );

    const mostRecentInternalDemoTeams = team?.team?.internalDemoTeams.sort((a, b) => {
      const aDate = new Date(a.createdAt);
      const bDate = new Date(b.createdAt);

      return bDate - aDate;
    });

    setInternalDemoTeam({
      active: internalDemoTeam,
      mostRecent: mostRecentInternalDemoTeams?.length ? mostRecentInternalDemoTeams[0] : null,
    });
  }, [team]);

  useEffect(() => {
    setIsLoading(true);

    getTeamById(props.teamId, async (err) => {
      if (!props.teamId) return;
      if (err) {
        alert(err);
      }

      // Get Stripe customer data if team has stripeCustomer ID
      if (team?.team?.stripeCustomer) {
        await getCustomerPaymentMethods(
          {
            stripeCustomerId: team.team.stripeCustomer,
          },
          (err) => {
            if (err) {
              console.error("Error fetching payment methods:", err);
            }
          },
        );
      }

      setIsLoading(false);
    });
  }, [props.teamId]);

  const handleSearchCrmExportLogs = async (teamId, searchQuery) => {
    setIsCrmExportLogsLoading(true);
    await getTeamCrmExportLogs(teamId, searchQuery, () => {
      setIsCrmExportLogsLoading(false);
    });
  };

  const makeAdmin = (userId, teamId, isAdmin) => {
    setIsLoading(true);
    setMemberAdmin(userId, teamId, isAdmin, (err) => {
      if (err) {
        alert(err);
      }
      // Refresh
      // Lookup Team by ID
      getTeamById(props.teamId, (err2) => {
        if (err2) {
          alert(err2);
        }
        setIsLoading(false);
      });
    });
  };

  // Get price config
  useEffect(() => {
    if (!price_config) {
      getPriceConfig((err) => {
        if (err) {
          alert(err);
        }
      });
    }
  }, []);

  const [customDevicesOrdered, setCustomDevicesOrdered] = useState(0);
  const [standardDevicesOrdered, setStandardDevicesOrdered] = useState(0);

  useEffect(() => {
    if (!team?.team?.orders) return;
    const orders = team?.team?.orders;

    const customDevices = orders.reduce((prev, current) => prev + current.customDevices, 0);
    const standardDevices = orders.reduce((prev, current) => prev + current.standardDevices, 0);

    setCustomDevicesOrdered(customDevices);
    setStandardDevicesOrdered(standardDevices);
  }, [team?.team?.orders]);

  const [isAddOrderScreen, setIsAddOrderScreen] = useState(false);

  // Inputs to add order
  const [shopifyOrderId, setShopifyOrderId] = useState("");
  const [fulfillOrderId, setFulfillOrderId] = useState("");
  const [newStandardDevices, setNewStandardDevices] = useState(0);
  const [newCustomDevices, setNewCustomDevices] = useState(0);

  const addOrder = () => {
    setIsLoading(true);

    const payload = {
      teamId: team.team.id,
      shopifyOrderId,
      fulfillOrderId,
      newStandardDevices,
      newCustomDevices,
    };

    addTeamOrder(payload, (err) => {
      if (err) {
        alert(err);
      } else {
        setShopifyOrderId("");
        setFulfillOrderId("");
        setNewStandardDevices(0);
        setNewCustomDevices(0);
        setIsAddOrderScreen(false);
      }

      // Refresh
      getTeamById(props.teamId, (err2) => {
        if (err2) {
          alert(err2);
        }
        setIsLoading(false);
      });
    });
  };

  const hasActiveSubscription = !!team?.stripeCustomer?.subscriptions?.data?.find(
    ({ id }) => id === team?.team?.stripeSubscription?.split("%")[1],
  );

  // Make links for env
  const [stripeBaseLink, setStripeBaseLink] = useState("");

  useEffect(() => {
    if (isProd) {
      setStripeBaseLink("https://dashboard.stripe.com/");
    } else {
      setStripeBaseLink("https://dashboard.stripe.com/test/");
    }
  }, [isProd]);

  const stripeOptions = {
    mode: "setup",
    currency: "usd",
    paymentMethodTypes: ["card"],
    disableLink: true,
  };

  const body = (
    <div>
      {stripePromise && (
        <Elements stripe={stripePromise} options={stripeOptions}>
          <StripeAddPaymentModal
            stripe={stripePromise}
            stripeCustomerId={team?.team?.stripeCustomer || team?.stripeCustomer?.id}
            open={openStripePaymentModal}
            closeModal={() => setOpenStripePaymentModal(false)}
          />
        </Elements>
      )}
      <h2 id="simple-modal-title">Team ID: #{team?.team?.id}</h2>

      <div
        style={{
          border: "1px solid gray",
          margin: "1rem 0",
          padding: "1rem",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            {editEmailOpen ? (
              <TextField
                required="true"
                variant="outlined"
                value={email}
                type="text"
                style={{ width: 400 }}
                label={"Email"}
                onChange={(e) => handleEmailChange(e.target.value)}
                disabled={isCommonActionLoading}
              />
            ) : (
              <p id="simple-modal-description">Email: {email}</p>
            )}

            <p>Plan Level: {planLookup(team?.team, price_config)}</p>
            <p>Member Count: {getMemberCount(team)}</p>
            <p>
              Stripe ID: {team?.team?.stripeCustomer}{" "}
              <span>
                <a
                  href={`${stripeBaseLink}customers/${team?.team?.stripeCustomer}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  | View in Stripe
                </a>
              </span>
            </p>
            <p>Account Executive: {teamSalesforceData?.accountExecutive.email || "None"}</p>
            <p>Customer Success: {teamSalesforceData?.accountCsm.email || "None"}</p>
          </div>

          <fieldset
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              border: 0,
              padding: "1rem 0",
              margin: 0,
            }}
          >
            <div className="buttonContainer" style={{ margin: 0 }} onClick={handleSetEmailOpen}>
              {editEmailOpen ? "Cancel" : "Edit Email"}
            </div>

            {editEmailOpen && (
              <button
                className="buttonContainer"
                style={{ margin: 0 }}
                onClick={() => {
                  handleUpdateTeam({ email });
                  handleSetEmailOpen(false);
                }}
                disabled={isCommonActionLoading}
              >
                Save
              </button>
            )}
          </fieldset>
        </div>

        <div style={{ marginTop: "1rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              marginBottom: "8px",
            }}
          >
            {user.can_access_sales && (
              <button
                onClick={isLoading ? null : (e) => changePlan(team?.team?.id)}
                disabled={isLoading}
                style={{
                  backgroundColor: "black",
                  color: "white",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                {isLoading ? (
                  <CircularProgress color="secondary" size={15} />
                ) : team?.stripeCustomer?.subscriptions?.data.length < 1
                  && internalDemoTeam.active ? (
                    "Update Trial/Quote"
                  ) : (
                    "Replace Plan"
                  )}
              </button>
            )}
            {team?.team?.enterprisePlan === "60" && (
              <button
                onClick={() => {
                  if (internalDemoTeam?.mostRecent?.multiYearSub) {
                    return snackbarAction({
                      message: "You can't currently upgrade to ELC with a multi-year subscription",
                      severity: "info",
                      duration: 3000,
                    });
                  }
                  setLeadCaptureUpgradeExpanded(!leadCaptureUpgradeExpanded);
                }}
                style={{
                  background: "none",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                {leadCaptureUpgradeExpanded ? "Collapse ELC upgrade" : "Expand ELC upgrade"}
              </button>
            )}

            <button
              onClick={() => setAdminControlsExpanded(!adminControlsExpanded)}
              style={{
                background: "none",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "4px 8px",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              {adminControlsExpanded ? "Collapse Admin Controls" : "Expand Admin Controls"}
            </button>
            <button
              onClick={async () => {
                const newExpandedState = !integrationsExpanded;
                setIntegrationsExpanded(newExpandedState);
                if (newExpandedState) {
                  setIsIntegrationsLoading(true);
                  await Promise.all([
                    getTeamIntegrationConnections(team?.team?.id, () => {
                      setIsIntegrationsLoading(false);
                    }),
                    getTeamCrmExportLogs(team?.team?.id, "", () => {
                      setIsCrmExportLogsLoading(false);
                    }),
                  ]);
                }
              }}
              style={{
                background: "none",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "4px 8px",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              {integrationsExpanded ? "Collapse Integrations" : "Expand Integrations"}
            </button>
          </div>

          <div style={{ width: adminControlsExpanded && !integrationsExpanded ? "60%" : "100%" }}>
            {adminControlsExpanded && user.can_access_admin && (
              <>
                <h4 style={{ marginBottom: "0.5rem" }}>Feature Flags</h4>
                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    padding: "0.75rem",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <FormGroup row style={{ alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isTextFollowUp}
                          onChange={(e) => handleUpdateTeam({ textFollowUp: e.target.checked })}
                          name="textFollowUp"
                          inputProps={{ "aria-label": "secondary checkbox" }}
                          disabled={isCommonActionLoading}
                        />
                      }
                      label={"Text Follow ups"}
                    />
                  </FormGroup>
                  <FormGroup row style={{ alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isEmailFollowUp}
                          onChange={(e) => handleUpdateTeam({ emailFollowUp: e.target.checked })}
                          name="emailFollowUp"
                          inputProps={{ "aria-label": "secondary checkbox" }}
                          disabled={isCommonActionLoading}
                        />
                      }
                      label={"Email Follow ups"}
                    />
                  </FormGroup>
                  <FormGroup row style={{ alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isSendWeeklyReminder}
                          onChange={(e) => handleUpdateTeam({ sendWeeklyInviteReminders: e.target.checked })
                          }
                          name="sendWeeklyReminder"
                          inputProps={{ "aria-label": "secondary checkbox" }}
                          disabled={isCommonActionLoading}
                        />
                      }
                      label={"Weekly Reminders"}
                    />
                  </FormGroup>
                </div>

                <h4 style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>Subscription Settings</h4>
                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    padding: "0.75rem",
                    backgroundColor: "#f9f9f9",
                    marginBottom: "1rem",
                  }}
                >
                  <form onSubmit={handleSubscriptionFormSubmit}>
                    <div style={{ marginBottom: "0.5rem" }}>
                      <TextField
                        fullWidth
                        label="Stripe Subscription"
                        variant="outlined"
                        size="small"
                        name="stripeSubscription"
                        value={teamSubscriptionFormValues.stripeSubscription || ""}
                        onChange={handleSubscriptionFormChange}
                        disabled={isCommonActionLoading}
                        helperText="Enter the stripe subscription ID (format: subItem%sub)"
                      />
                    </div>
                    <div style={{ marginBottom: "0.5rem" }}>
                      <TextField
                        fullWidth
                        label="Enterprise Plan"
                        variant="outlined"
                        size="small"
                        name="enterprisePlan"
                        value={teamSubscriptionFormValues.enterprisePlan || ""}
                        onChange={handleSubscriptionFormChange}
                        disabled={isCommonActionLoading}
                        helperText="Enterprise plan level (e.g. 60, 50)"
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "0.5rem",
                      }}
                    >
                      <button
                        type="submit"
                        style={{
                          background: "#2196f3",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          padding: "6px 16px",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                        disabled={isCommonActionLoading}
                      >
                        Save
                      </button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>

          {integrationsExpanded && (
            <IntegrationsSection
              integrationConnections={integrationConnections}
              crmExportLogs={crmExportLogs}
              isIntegrationsLoading={isIntegrationsLoading}
              isCrmExportLogsLoading={isCrmExportLogsLoading}
              onSearchCrmExportLogs={handleSearchCrmExportLogs}
              entityId={team?.team?.id}
            />
          )}

          {user.can_access_sales
            && team?.team?.enterprisePlan === "60"
            && leadCaptureUpgradeExpanded && (
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "0.75rem",
                backgroundColor: "#f9f9f9",
                marginTop: "1rem",
              }}
            >
              <h4 style={{ marginBottom: "0.5rem" }}>Lead Capture Upgrade</h4>
              <LeadCaptureUpgradeSection
                team={team}
                customerPaymentMethods={stripe_customer_payment_methods}
                internalDemoTeam={internalDemoTeam.mostRecent}
                hasActiveSubscription={hasActiveSubscription}
                setOpenStripePaymentModal={setOpenStripePaymentModal}
                stripeCustomer={team.stripeCustomer}
                stripePromise={stripePromise}
                setIsLoading={setIsLoading}
                changePlan={changePlan}
                isLoading={isLoading}
                isPriceMonthly={isPriceMonthly}
                selectedPlanInterval={selectedPlanInterval}
              />
            </div>
          )}
        </div>
      </div>

      {(hasActiveSubscription || internalDemoTeam.active?.trialExpiresAtUtc) && (
        <div
          style={{
            border: "1px solid gray",
            margin: "1rem 0",
            padding: "1rem",
          }}
        >
          <h3 style={{ marginTop: 0 }}>Subscription Details</h3>
          {!hasActiveSubscription && internalDemoTeam.active?.trialExpiresAtUtc ? (
            <div>
              <p>On a free trial with no active subscription</p>
              {internalDemoTeam.active?.trialExpiresAtUtc && !isPriceMonthly && (
                <ExtendTrialButton
                  internalDemoTeam={internalDemoTeam.active}
                  setIsLoading={setIsLoading}
                />
              )}
            </div>
          ) : (
            team?.stripeCustomer?.subscriptions?.data?.map((subscription, index) => (
              <div
                key={subscription.id}
                style={
                  index > 0
                    ? { marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #e0e0e0" }
                    : {}
                }
              >
                <p>
                  Subscription ID: {subscription.id}{" "}
                  <span>
                    <a
                      href={`${stripeBaseLink}subscriptions/${subscription.id}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      View in Stripe
                    </a>
                  </span>
                </p>
                <p>Status: {subscription.status}</p>
                <p>
                  Current Period Start:{" "}
                  {new Date(subscription.current_period_start * 1000).toLocaleDateString()}
                </p>
                <p>
                  Current Period End:{" "}
                  {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
                </p>
                <p>
                  Total Subscription Amount: $
                  {subscription.items.data.reduce(
                    (acc, item) => (item?.price?.unit_amount || 0) * item.quantity + acc,
                    0,
                  ) / 100}
                </p>
                <p>
                  Cycle: <b>{isPriceMonthly ? "month" : "year"}ly</b>
                </p>
                {internalDemoTeam?.active?.trialExpiresAtUtc
                  && subscription.items.data[0].price.recurring.interval === "year" && (
                  <ExtendTrialButton
                    internalDemoTeam={internalDemoTeam?.active}
                    setIsLoading={setIsLoading}
                  />
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Orders */}
      <div
        style={{
          border: "1px solid gray",
          margin: "1rem 0",
          padding: "1rem",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Orders</h3>
        <div>
          <p>
            Customs Ordered: <b>{customDevicesOrdered}</b>
          </p>
          <p>
            Standards Ordered: <b>{standardDevicesOrdered}</b>
          </p>
          {user.can_access_ops && (
            <div
              className="buttonContainerAlt"
              onClick={isLoading ? null : (e) => setIsAddOrderScreen(true)}
              style={{ width: "fit-content" }}
            >
              Add Order
            </div>
          )}
        </div>
      </div>

      {team?.team?.memberTeams?.length > 0 && (
        <div
          style={{
            border: "1px solid gray",
            margin: "1rem 0",
            padding: "1rem",
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            Team Members ({team?.team?.memberTeams?.length} / {getMemberCount(team)})
          </h3>
          {team?.team?.memberTeams.map((memberTeam, index) => (
            <div
              key={memberTeam?.member?.id}
              style={{
                padding: "0.75rem",
                marginBottom: "0.75rem",
                backgroundColor: "#f9f9f9",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div>
                <p style={{ margin: "0.25rem 0" }}>Member ID: {memberTeam?.member?.id}</p>
                <p style={{ margin: "0.25rem 0" }}>Member Email: {memberTeam?.member?.email}</p>
                <p style={{ margin: "0.25rem 0" }}>
                  Is Admin: {memberTeam?.isAdmin ? "Yes" : "No"}
                </p>
                <div>
                  <p>Last Login: {dates.getRelativeLoginDateForUser(memberTeam?.member)}</p>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  justifyContent: "center",
                }}
              >
                {user.can_access_search && (
                  <div
                    className="buttonContainerAlt"
                    onClick={() => props.openUserFn(memberTeam?.member?.id)}
                  >
                    Go to Member
                  </div>
                )}
                {user.can_access_admin && (
                  <div style={{ alignSelf: "center" }}>
                    {memberTeam?.isAdmin ? (
                      <div
                        className="buttonContainerAlt"
                        onClick={
                          isLoading
                            ? null
                            : (e) => makeAdmin(memberTeam?.member?.id, team?.team?.id, false)
                        }
                      >
                        {isLoading ? (
                          <CircularProgress color="secondary" size={15} />
                        ) : (
                          "Remove Admin"
                        )}
                      </div>
                    ) : (
                      <div
                        className="buttonContainerAlt"
                        onClick={
                          isLoading
                            ? null
                            : (e) => makeAdmin(memberTeam?.member?.id, team?.team?.id, true)
                        }
                      >
                        {isLoading ? (
                          <CircularProgress color="secondary" size={15} />
                        ) : (
                          "Make Admin"
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const addOrderBody = (
    <div>
      <h2 id="simple-modal-title">Team ID: #{team?.team?.id}</h2>
      <p id="simple-modal-description">Owner Email: {team?.team?.email}</p>
      <p>Plan Level: {planLookup(team?.team, price_config)}</p>
      <p>Member Count: {getMemberCount(team)}</p>

      {/* Orders */}
      <h3>Orders</h3>
      <hr />
      <div>
        <p>
          Customs Ordered: <b>{customDevicesOrdered}</b>
        </p>
        <p>
          Standards Ordered: <b>{standardDevicesOrdered}</b>
        </p>
      </div>
      <hr />
      <h3>Details</h3>

      <TextField
        required="true"
        variant="outlined"
        value={shopifyOrderId}
        label="Shopify Order ID"
        onChange={(e) => setShopifyOrderId(e.target.value)}
        disabled={isLoading}
      />
      <br />
      <br />
      <TextField
        required="true"
        variant="outlined"
        value={fulfillOrderId}
        label="Fulfil Order ID"
        onChange={(e) => setFulfillOrderId(e.target.value)}
        disabled={isLoading}
      />

      <h3>Devices</h3>

      <TextField
        required="true"
        variant="outlined"
        value={newStandardDevices}
        type="number"
        label={"New Standard Devices"}
        onChange={(e) => setNewStandardDevices(parseInt(e.target.value, 10))}
        disabled={isLoading}
      />
      <br />
      <br />
      <TextField
        required="true"
        variant="outlined"
        value={newCustomDevices}
        type="number"
        label={"New Custom Devices"}
        onChange={(e) => setNewCustomDevices(parseInt(e.target.value, 10))}
        disabled={isLoading}
      />
      <br />
      <br />

      <div className="buttonContainerAlt" onClick={isLoading ? null : (e) => addOrder()}>
        Add Order
      </div>
      <br />
      <br />
      <div
        className="buttonContainer"
        onClick={isLoading ? null : (e) => setIsAddOrderScreen(false)}
      >
        {isLoading ? <CircularProgress color="secondary" size={15} /> : "Back"}
      </div>
    </div>
  );

  return (
    <Modal open={props.open} onClose={props.closeModal}>
      <div style={modalStyle} className={classes.paper}>
        {isLoading || isCommonActionLoading ? (
          <CircularProgress />
        ) : isAddOrderScreen ? (
          addOrderBody
        ) : (
          body
        )}
      </div>
    </Modal>
  );
}
